/**
 * @file Renders the alert that lists an investment's incomplete-profile errors.
 */
import React from "react";
import { Alert, List, Typography, Space, Row, Col } from "antd";

const LABEL_TITLE = "Incomplete Profile";
const LABEL_RESOLVE = "Resolve";

const ALERT_STYLE = { padding: "14px 18px" };
const ITEM_STYLE = { padding: "2px 0px 2px 0px", border: "none" };

type ErrorItem = {
  key: number;
  text: string;
};

type Props = {
  title?: string;
  errors?: string[];
  isFunded: boolean;
  onResolve?: () => void;
};

/**
 * Renders an alert listing the investment's outstanding errors, with a link to resolve them.
 */
const InvestmentErrors = ({
  title = LABEL_TITLE,
  errors = [],
  isFunded,
  onResolve,
}: Props): JSX.Element | null => {
  const dataSource: ErrorItem[] = errors.map((text, key) => ({ key, text }));
  const hasErrors = dataSource.length > 0;

  if (!hasErrors) {
    return null;
  }

  if (isFunded) {
    return null;
  }

  /**
   * Renders one error as a bulleted list item.
   */
  const renderItem = (item: ErrorItem): JSX.Element => (
    <List.Item style={ITEM_STYLE}>
      <Space>
        <Typography.Text strong>&bull;</Typography.Text>
        <Typography.Text>{item.text}</Typography.Text>
      </Space>
    </List.Item>
  );

  const description = (
    <Row>
      <Col flex="auto">
        <List
          size="small"
          itemLayout="vertical"
          dataSource={dataSource}
          renderItem={renderItem}
        />
      </Col>
      <Col>
        <Typography.Link onClick={onResolve}>
          {LABEL_RESOLVE}
        </Typography.Link>
      </Col>
    </Row>
  );

  return (
    <Alert
      type="error"
      style={ALERT_STYLE}
      message={title}
      description={description}
    />
  );
};

export default InvestmentErrors;
